import React from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store";

interface Step3Props {
  data: Record<string, any>;
  onUpdate: (updated: Record<string, any>) => void;
}

const Step3PaymentMode: React.FC<Step3Props> = ({ data, onUpdate }) => {
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    if (type === "checkbox" && e.target instanceof HTMLInputElement) {
      onUpdate({ [name]: e.target.checked });
    } else {
      onUpdate({ [name]: value });
    }
  };

  const attendanceMetadata = useSelector(
    (state: RootState) => state.attendance.attendanceMetadata
  );

  const banks = attendanceMetadata?.banks || [];
  const paymentTypes = attendanceMetadata?.paymentTypes || [];

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-md">
      <h2 className="text-xl font-semibold text-gray-900 mb-6 pb-2 border-b border-gray-200">
        Payment & Bank Details
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <InputField
          label="Payment Type"
          name="paymentType"
          value="Bank Transfer"
          onChange={() => {}}
          className="bg-gray-100 cursor-not-allowed"
          readOnly
        />

        <SelectField
          label="Bank Name *"
          name="bankName"
          value={data.bankName}
          options={banks}
          onChange={handleChange}
        />
        <InputField
          label="Branch *"
          name="branch"
          value={data.branch}
          placeholder="e.g. MG Road"
          onChange={handleChange}
        />
        <InputField
          label="Account Number *"
          name="accountNo"
          value={data.accountNo}
          placeholder="e.g. 1234567890"
          onChange={handleChange}
        />
        <InputField
          label="IFSC Code *"
          name="ifsc"
          value={data.ifsc}
          placeholder="e.g. SBIN0001234"
          className="uppercase"
          onChange={handleChange}
        />
        <InputField
          label="Account Holder Name *"
          name="accountHolderName"
          value={data.accountHolderName}
          placeholder="e.g. John Doe"
          onChange={handleChange}
        />
        <InputField
          label="UAN Number"
          name="uanNumber"
          value={data.uanNumber}
          placeholder="100123456789"
          onChange={handleChange}
        />
      </div>

      <div className="mt-6 flex flex-col md:flex-row md:items-center gap-6 border-t pt-4">
        <CheckboxField
          label="Include PF"
          name="includePf"
          checked={data.includePf}
          onChange={handleChange}
        />
        <CheckboxField
          label="Include ESI"
          name="includeEsi"
          checked={data.includeEsi}
          onChange={handleChange}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
        {data.pf && (
          <InputField
            label="PF Number"
            name="pfNumber"
            value={data.pfNumber}
            placeholder="e.g. PF123456789"
            onChange={handleChange}
          />
        )}
        {data.esi && (
          <InputField
            label="ESI Number"
            name="esiNumber"
            value={data.esiNumber}
            placeholder="e.g. ESI987654321"
            onChange={handleChange}
          />
        )}
      </div>
    </div>
  );
};

const InputField = ({
  label,
  name,
  value,
  placeholder,
  onChange,
  type = "text",
  className = "",
}: any) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1 tracking-wide">
      {label}
    </label>
    <input
      type={type}
      name={name}
      value={value || ""}
      placeholder={placeholder}
      onChange={onChange}
      className={`w-full px-3 py-2 border border-gray-300 bg-gray-50 rounded-md shadow-sm 
        focus:outline-none focus:ring-2 focus:ring-blue-500 
        transition duration-200 ease-in-out hover:border-gray-400 ${className}`}
    />
  </div>
);

const SelectField = ({ label, name, value, options, onChange }: any) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1 tracking-wide">
      {label}
    </label>
    <select
      name={name}
      value={value || ""}
      onChange={onChange}
      className="w-full px-3 py-2 border border-gray-300 bg-gray-50 rounded-md shadow-sm 
        focus:outline-none focus:ring-2 focus:ring-blue-500 
        transition duration-200 ease-in-out hover:border-gray-400"
    >
      <option value="">Select</option>
      {options.map((opt: string) => (
        <option key={opt} value={opt.toLowerCase().replace(/\s+/g, "_")}>
          {opt}
        </option>
      ))}
    </select>
  </div>
);

export const CheckboxField = ({
  label,
  name,
  checked,
  onChange,
}: {
  label: string;
  name: string;
  checked: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) => (
  <div className="flex items-center">
    <input
      id={name}
      name={name}
      type="checkbox"
      checked={checked || false}
      onChange={onChange}
      className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500 transition duration-150"
    />
    <label htmlFor={name} className="ml-2 text-sm text-gray-700">
      {label}
    </label>
  </div>
);

export default Step3PaymentMode;
